import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, X, FileText, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PDFManager = ({ pdfs = [], onPDFsUpdate }) => {
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pdfTitle, setPdfTitle] = useState('');
  const MAX_PDFS = 5;

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Please select a PDF file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('PDF must be less than 10MB');
      return;
    }

    if (!pdfTitle.trim()) {
      toast.error('Please enter a title for the PDF');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', 'pdf');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/upload/file`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });

      const newPdf = {
        title: pdfTitle.trim(),
        url: response.data.url,
        filename: file.name
      };

      onPDFsUpdate([...pdfs, newPdf]);
      setPdfTitle('');
      setShowUpload(false);
      toast.success('PDF uploaded successfully');
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload PDF');
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePDF = (index) => {
    const newPdfs = pdfs.filter((_, i) => i !== index);
    onPDFsUpdate(newPdfs);
    toast.success('PDF removed');
  };

  return (
    <div className="space-y-4" data-testid="pdf-manager">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-white">Documents ({pdfs.length}/{MAX_PDFS} PDFs)</label>
        {pdfs.length < MAX_PDFS && !showUpload && (
          <Button onClick={() => setShowUpload(true)} size="sm" className="bg-primary" data-testid="add-pdf-button">
            <Plus className="w-4 h-4 mr-1" /> Add PDF
          </Button>
        )}
      </div>

      {showUpload && (
        <div className="bg-zinc-800/50 border border-white/10 rounded-xl p-4 space-y-4">
          <div>
            <Label className="text-white text-sm">PDF Title *</Label>
            <Input
              value={pdfTitle}
              onChange={(e) => setPdfTitle(e.target.value)}
              placeholder="e.g., Resume, Portfolio, Brochure"
              className="bg-white/5 border-white/10 text-white mt-1"
              data-testid="pdf-title-input"
            />
          </div>
          
          <div className="relative">
            <input
              type="file"
              accept=".pdf,application/pdf"
              onChange={handleFileSelect}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              disabled={uploading || !pdfTitle.trim()}
              data-testid="pdf-file-input"
            />
            <div className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${pdfTitle.trim() ? 'border-primary/50 hover:border-primary' : 'border-white/10'}`}>
              {uploading ? (
                <div className="flex flex-col items-center">
                  <Loader2 className="w-8 h-8 text-primary animate-spin mb-2" />
                  <p className="text-sm text-white">Uploading...</p>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                  <p className="text-sm text-white mb-1">
                    {pdfTitle.trim() ? 'Click or drag PDF here' : 'Enter title first'}
                  </p>
                  <p className="text-xs text-muted-foreground">Max 10MB</p>
                </div>
              )}
            </div>
          </div>

          <Button onClick={() => { setShowUpload(false); setPdfTitle(''); }} variant="ghost" size="sm" className="text-muted-foreground">
            Cancel
          </Button>
        </div>
      )}

      {pdfs.length > 0 && (
        <div className="space-y-3">
          {pdfs.map((pdf, index) => (
            <div key={index} className="flex items-center gap-3 bg-white/5 rounded-xl p-4 group" data-testid={`pdf-item-${index}`}>
              <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-red-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{pdf.title}</p>
                <p className="text-xs text-muted-foreground truncate">{pdf.filename}</p>
              </div>
              <button 
                onClick={() => handleRemovePDF(index)} 
                className="w-8 h-8 rounded-full bg-destructive/20 hover:bg-destructive flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
                data-testid={`remove-pdf-${index}`}
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          ))}
        </div>
      )}

      {pdfs.length === 0 && !showUpload && (
        <div className="text-center py-8 border border-dashed border-white/10 rounded-xl" data-testid="empty-pdfs">
          <FileText className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground">No documents uploaded</p>
          <p className="text-xs text-muted-foreground mt-1">Add PDFs like resumes, portfolios, or brochures</p>
        </div>
      )}
    </div>
  );
};

export default PDFManager;
